import { useMemo, useCallback, useState } from 'react';
import { useApp } from '@/context/AppContext';
import { useToday } from '@/hooks/useToday';
import type { Task, Badge } from '@trackmind/core';
import {
  completeTaskEffect,
  uncompleteTaskEffect,
  createTask as createTaskRecord,
  coerceTaskInput,
  steppedCount,
  applyTaskUpdate,
} from '@trackmind/core';


export interface XPGain {
  xpEarned: number;
  newLevel?: number;
  newBadges: Badge[];
}

export interface CreateTaskInput {
  project_id: string;
  title: string;
  description?: string;
  priority?: number;
  difficulty?: number;
  energy_level?: 'low' | 'medium' | 'high';
  estimated_minutes?: number;
  has_deadline?: boolean;
  deadline?: string;
  parent_task_id?: string;
  is_habit?: boolean;
  recurrence?: {
    frequency?: 'daily' | 'weekly' | 'monthly';
    days_of_week?: string[];
    time_of_day?: string;
    end_date?: string;
    /**
     * Monthly habits only. The form has offered this since the day-of-month
     * picker landed, but it was missing here — so every write below rebuilt
     * `recurrence` field by field and silently dropped it, and a monthly
     * habit fell back to "the day it was created on" no matter what was
     * typed. Null means exactly that fallback; a number pins the day.
     */
    day_of_month?: number | null;
  };
  tags?: string[];
  needs_review?: boolean;
  dependencies?: string[];
  status?: 'todo' | 'in_progress' | 'completed';
  // Fixed time scheduling (meetings, appointments)
  is_pinned?: boolean;
  pin_type?: 'soft' | 'hard';
  scheduled_for?: string;
  // Countable progress ("read 5 papers")
  target_count?: number | null;
  count_unit?: string | null;
}

/**
 * Map the form's CreateTaskInput onto model fields for an update.
 *
 * The two shapes differ deliberately: the form has `has_deadline` and uses
 * `undefined` for "not set", while Task uses `null` and has no such flag.
 * Only keys actually present are forwarded, so an update never blanks a field
 * the form didn't ask about.
 *
 * Input is coerced first. For anything the form produces that is a no-op —
 * a `<select>` cannot emit priority 9 — but this is also the door an import
 * or an assistant comes through, and an unusable value should cost its own
 * field, not the whole write.
 */
export function taskInputToPatch(input: CreateTaskInput): Partial<Task> {
  const data = coerceTaskInput(input);
  const patch: Partial<Task> = {};
  const copy = <K extends keyof Task>(key: K, value: Task[K] | undefined) => {
    if (value !== undefined) patch[key] = value;
  };

  copy('project_id', data.project_id);
  copy('title', data.title);
  copy('description', data.description);
  copy('priority', data.priority);
  copy('difficulty', data.difficulty);
  copy('energy_level', data.energy_level);
  copy('estimated_minutes', data.estimated_minutes);
  copy('status', data.status);
  copy('dependencies', data.dependencies);
  copy('tags', data.tags);
  copy('is_habit', data.is_habit);
  copy('needs_review', data.needs_review);
  copy('is_pinned', data.is_pinned);
  copy('pin_type', data.pin_type);
  copy('target_count', data.target_count ?? null);
  copy('count_unit', data.count_unit ?? null);
  copy('parent_task_id', data.parent_task_id ?? null);
  copy('scheduled_for', data.scheduled_for ?? null);

  // `has_deadline: false` is an explicit instruction to clear it.
  if (data.has_deadline === false) {
    patch.deadline = null;
  } else if (data.deadline !== undefined) {
    patch.deadline = data.deadline;
  }

  if (data.recurrence) {
    patch.recurrence = {
      frequency: data.recurrence.frequency ?? null,
      days_of_week: data.recurrence.days_of_week ?? [],
      time_of_day: data.recurrence.time_of_day ?? null,
      end_date: data.recurrence.end_date ?? null,
      day_of_month: data.recurrence.day_of_month ?? null,
    };
  }

  return patch;
}

export function useTasks(projectId?: string) {
  const {
    tasks, saveTask, deleteTask: removeTask, stats, saveStats,
    habitInstances, saveHabitInstance, deleteHabitInstance,
    schedules, saveSchedule, preferences,
  } = useApp();
  const todayKey = useToday();
  const [lastXPGain, setLastXPGain] = useState<XPGain | null>(null);

  // Filter tasks by project if specified
  const filteredTasks = useMemo(() => {
    if (!projectId) return tasks;
    return tasks.filter(t => t.project_id === projectId);
  }, [tasks, projectId]);

  const createTask = useCallback(async (input: CreateTaskInput): Promise<Task> => {
    const data = coerceTaskInput(input);
    // createTask in core builds the Task. This function only translates the
    // form's shape into the model's — `has_deadline` is a form concept, and
    // `undefined` means "not set" here where the model uses `null`.
    //
    // It used to construct the object itself, field by field, which made it a
    // second implementation with four quiet differences from the factory:
    // priority defaulted to 5 (the HIGHEST) instead of 3, description to ''
    // instead of null, and is_archived/archived_at were missing entirely — so
    // a task made in the app had a different shape from one made anywhere
    // else. Every UI path passes a priority, so nothing visible moves.
    const newTask = createTaskRecord({
      project_id: data.project_id,
      parent_task_id: data.parent_task_id ?? null,
      title: data.title,
      description: data.description,
      priority: data.priority,
      difficulty: data.difficulty,
      energy_level: data.energy_level,
      estimated_minutes: data.estimated_minutes,
      dependencies: data.dependencies,
      tags: data.tags,
      deadline: data.has_deadline === false ? null : data.deadline,
      scheduled_for: data.scheduled_for,
      is_habit: data.is_habit,
      recurrence: data.recurrence,
      needs_review: data.needs_review,
      is_pinned: data.is_pinned,
      pin_type: data.pin_type,
      target_count: data.target_count,
      count_unit: data.count_unit,
    });
    await saveTask(newTask);
    return newTask;
  }, [saveTask]);

  const updateTask = useCallback(async (id: string, data: Partial<Task>): Promise<Task> => {
    const existing = tasks.find(t => t.id === id);
    if (!existing) {
      throw new Error(`Task ${id} not found`);
    }

    // applyTaskUpdate owns the merge: keys the caller didn't mention are left
    // alone, `null` clears, and the timestamp is not something a caller can
    // forget. The assistant edits tasks through the same function.
    const updated = applyTaskUpdate(existing, data, new Date().toISOString());
    await saveTask(updated);
    return updated;
  }, [tasks, saveTask]);

  /**
   * One-tap increment/decrement of a countable task ("read 5 papers" → 3/5).
   * Clamped to [0, ∞); completion stays a deliberate act via the checkbox —
   * hitting the target fills the bar but doesn't silently complete the task.
   */
  const stepTaskCount = useCallback(async (id: string, delta: number): Promise<Task> => {
    const existing = tasks.find(t => t.id === id);
    if (!existing) {
      throw new Error(`Task ${id} not found`);
    }
    return updateTask(id, { current_count: steppedCount(existing, delta) });
  }, [tasks, updateTask]);

  /**
   * Complete a task or habit.
   * @param id - Task/habit ID
   * @param options - Optional settings
   * @param options.actualMinutes - Actual time spent on the task
   * @param options.forDate - For habits: the scheduled date to record completion for (defaults to today).
   *                          This allows completing a habit scheduled for a previous day.
   */
  const completeTask = useCallback(async (
    id: string,
    options?: { actualMinutes?: number; forDate?: string; workoutId?: string },
  ): Promise<Task> => {
    const existing = tasks.find(t => t.id === id);
    if (!existing) {
      throw new Error(`Task ${id} not found`);
    }

    // Every consequence — the habit instance and its timing snapshot, XP,
    // badges, the streak, the day's plan — is decided by completeTaskEffect
    // in core. This function's whole job is to store what comes back. It
    // does not get to choose any of it, and it cannot skip a piece: dropping
    // one is exactly how the assistant's completions stopped ticking the
    // schedule while looking like they had worked.
    const outcome = completeTaskEffect({
      task: existing,
      tasks,
      habitInstances,
      getSchedule: (date) => schedules.get(date) ?? null,
      stats,
      // The user's today (their time zone setting), as every other screen
      // and the assistant use — the same zone the completion is read in.
      today: todayKey(),
      forDate: options?.forDate,
      actualMinutes: options?.actualMinutes ?? null,
      workoutId: options?.workoutId,
      timezone: preferences?.timezone,
    });

    // Already done. A second press must not move the totals.
    if (!outcome.changed) return existing;

    if (outcome.habitInstance) await saveHabitInstance(outcome.habitInstance);
    await saveTask(outcome.task);
    await saveStats(outcome.stats);
    if (outcome.schedule) await saveSchedule(outcome.schedule);

    if (outcome.xpEarned > 0) {
      setLastXPGain({
        xpEarned: outcome.xpEarned,
        newLevel: outcome.newLevel,
        newBadges: outcome.newBadges,
      });
    }

    return outcome.task;
  }, [tasks, stats, habitInstances, saveTask, saveStats, saveHabitInstance,
      schedules, saveSchedule, preferences?.timezone, todayKey]);

  /**
   * Take a completion back.
   *
   * The mirror of the above, and equally hands-off: `uncompleteTaskEffect`
   * decides what to restore, how much XP to reverse (the amount recorded at
   * completion, not a recomputation), what the streak becomes once the date
   * is gone, and whether the day's plan changes. Three of those have rules
   * that are easy to get wrong and were got wrong before — badges are never
   * revoked, longest_streak never falls, and the streak is recomputed from
   * remaining history rather than decremented.
   */
  const uncompleteTask = useCallback(async (
    id: string,
    options?: { forDate?: string },
  ): Promise<Task | null> => {
    const existing = tasks.find(t => t.id === id);
    if (!existing) return null;

    const outcome = uncompleteTaskEffect({
      task: existing,
      tasks,
      habitInstances,
      getSchedule: (date) => schedules.get(date) ?? null,
      stats,
      today: todayKey(),
      forDate: options?.forDate,
      timezone: preferences?.timezone,
    });

    if (!outcome.changed) return existing;

    if (outcome.removeInstance) {
      await deleteHabitInstance(outcome.removeInstance.habitId, outcome.removeInstance.date);
    } else {
      await saveTask(outcome.task);
    }
    await saveStats(outcome.stats);
    if (outcome.schedule) await saveSchedule(outcome.schedule);

    return outcome.task;
  }, [tasks, stats, habitInstances, saveTask, saveStats, deleteHabitInstance,
      schedules, saveSchedule, preferences?.timezone, todayKey]);

  const clearXPGain = useCallback(() => {
    setLastXPGain(null);
  }, []);

  // Filter helpers
  const regularTasks = useMemo(() => filteredTasks.filter(t => !t.is_habit), [filteredTasks]);
  const habits = useMemo(() => filteredTasks.filter(t => t.is_habit), [filteredTasks]);
  const todoTasks = useMemo(() => filteredTasks.filter(t => t.status === 'todo'), [filteredTasks]);
  const completedTasks = useMemo(() => filteredTasks.filter(t => t.status === 'completed'), [filteredTasks]);
  const inProgressTasks = useMemo(() => filteredTasks.filter(t => t.status === 'in_progress'), [filteredTasks]);
  const inboxTasks = useMemo(() => filteredTasks.filter(t => t.needs_review && t.status !== 'completed'), [filteredTasks]);

  return {
    tasks: filteredTasks,
    regularTasks,
    habits,
    todoTasks,
    completedTasks,
    inProgressTasks,
    inboxTasks,
    loading: false,
    error: null,
    refetch: () => Promise.resolve(),
    createTask,
    updateTask,
    stepTaskCount,
    deleteTask: removeTask,
    completeTask,
    uncompleteTask,
    lastXPGain,
    clearXPGain,
  };
}
